import React, { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { toggleExpandedForAll } from 'react-sortable-tree';
import MainTitle from 'base/components/MainTitle/index';
import Header from 'base/components/Header/index';
import Flex from 'base/components/Flex/index';
import PermissionTree from 'admin/permissions/components/PermissionTree';
import {
  fetchPermissionsBySid,
  setPermissionTree,
  fetchAclSids,
  setPermissions,
  fetchAclObjectIdentities,
  setEditingRoleId,
} from 'auth/store/permissions/actions';
import { confirmRoleAudit, fetchAuditPendingRoleIdsForSpaceId } from 'auth/store/audit/actions';
import {
  getEditingSid,
  getEditingRole,
  getPermissionTree,
  getManagePermissionsIsLoading,
} from 'auth/store/permissions/selectors';
import { getActiveStateAuditPendingRoleIds } from 'auth/store/audit/selectors';
import { fetchAllSpaceRoles } from 'auth/store/roles/actions';
import { getActiveFolderTree } from 'documents/store/folders/selectors';
import { fetchFolderTree } from 'documents/store/folders/actions';
import HeaderMessage from 'base/components/HeaderMessage';
import Button from 'base/components/Button';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMatch } from 'react-router';
import { getActiveSpace } from '../../store/selectors';
import theme from '../../../theme';

export default function SpacePermissions() {
  const dispatch = useAppDispatch();
  const space = useAppSelector(getActiveSpace);
  const treeData = useAppSelector(getPermissionTree);
  const activeFolderTree = useAppSelector(getActiveFolderTree);
  const sid = useAppSelector(getEditingSid);
  const role = useAppSelector(getEditingRole);
  const isLoading = useAppSelector(getManagePermissionsIsLoading);
  const roleIdsPendingAudit = useAppSelector(getActiveStateAuditPendingRoleIds);
  const match = useMatch('/spaces/:spaceId/permissions/*');
  const spaceId = match?.params?.spaceId;
  const roleMatch = useMatch('/spaces/:spaceId/permissions/:roleId');
  const roleMatchId = roleMatch?.params?.roleId;

  const buildPermissionTree = useCallback(() => {
    if (space?.id && activeFolderTree && activeFolderTree.length > 0 && isLoading === false) {
      const treeData = [
        {
          type: 'space',
          name: space.name,
          id: space.id,
          children: activeFolderTree,
        },
      ];
      dispatch(setPermissionTree(treeData));
    }
  }, [isLoading, dispatch, space?.id, space?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    buildPermissionTree();
  }, [isLoading, buildPermissionTree]);

  useEffect(() => {
    buildPermissionTree();
  }, [buildPermissionTree]);

  const handleTreeChange = useCallback(
    treeData => {
      dispatch(setPermissionTree(treeData));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(fetchAllSpaceRoles(spaceId));
    dispatch(setEditingRoleId());
    dispatch(fetchAclSids());
    dispatch(fetchAclObjectIdentities());
    dispatch(fetchFolderTree(spaceId));
    buildPermissionTree();
    dispatch(fetchAuditPendingRoleIdsForSpaceId(spaceId));
  }, [dispatch, spaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(setPermissions({}));
    if (sid !== null) {
      dispatch(fetchPermissionsBySid(sid));
    }
  }, [sid, dispatch]);

  useEffect(() => {
    buildPermissionTree();
    dispatch(fetchFolderTree(spaceId));
    dispatch(fetchAuditPendingRoleIdsForSpaceId(spaceId));
  }, [spaceId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(setEditingRoleId(roleMatchId));
    // Collapse all tree nodes
    const collapsedTree = toggleExpandedForAll({
      treeData,
      expanded: false,
    });
    dispatch(setPermissionTree(collapsedTree));
  }, [dispatch, roleMatchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleAudit = useCallback(() => {
    dispatch(confirmRoleAudit(spaceId, role));
  }, [dispatch, role, spaceId]);

  return (
    <>
      <Header>
        <MainTitle>
          Space {space && space.name} permissions <br />
          <span style={{ color: theme.colors.primary }}>{role.name}</span>
        </MainTitle>
      </Header>

      {space &&
        space.needsRoleAudit &&
        space.auditDeadline &&
        roleIdsPendingAudit.includes(role.id) &&
        (new Date(space.auditDeadline) < new Date() ? (
          <HeaderMessage
            type="warning"
            title="Role review deadline expired"
            message={
              <div>
                <p>
                  <strong>
                    Corresponding users/roles have been blocked from this space.
                    <br />
                  </strong>
                  Please review the permissions below to allow all users to access this space again.
                  <br />
                  If you find any discrepancies, please contact an administrator. Otherwise, click on the button below
                  to confirm that everything is correct.
                </p>
                <Button variant="white" onClick={handleRoleAudit}>
                  All users and assigned roles are correct
                </Button>
              </div>
            }
          />
        ) : (
          <HeaderMessage
            type="warning"
            title="Role review required"
            message={
              <div>
                <p>
                  Please review the permissions below until&nbsp;
                  {dayjs(space.auditDeadline).format('DD.MM.YYYY')}
                  &nbsp;, or they will be blocked from accessing this space.
                  <br />
                  If you find any discrepancies, please contact an administrator. Otherwise, click on the button below
                  to confirm that everything is correct.
                </p>
                <Button variant="white" onClick={handleRoleAudit}>
                  All users and assigned roles are correct
                </Button>
              </div>
            }
          />
        ))}

      <Flex>
        {isLoading ? (
          'Loading...'
        ) : (
          <PermissionTree
            treeData={treeData}
            onChange={handleTreeChange}
            generateNodeProps={() => ({
              readOnly: true,
            })}
          />
        )}
      </Flex>
    </>
  );
}
